import { Autocomplete, TextField, Checkbox, Chip, Box } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

export interface MultiSelectOption {
  value: string;
  label: string;
  // Matched instead of `label` when searching, for options that hide details from their label.
  searchText?: string;
}

interface MultiSelectFilterProps {
  label: string;
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  searchable?: boolean;
  placeholder?: string;
  size?: "small" | "medium";
  width?: number;
  disabled?: boolean;
}

export default function MultiSelectFilter({
  label,
  options,
  value,
  onChange,
  searchable = false,
  placeholder = "All",
  size = "small",
  width = 220,
  disabled = false,
}: MultiSelectFilterProps) {
  const selectedOptions = options.filter((o) => value.includes(o.value));

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      openOnFocus={!searchable}
      size={size}
      options={options}
      value={selectedOptions}
      disabled={disabled}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      onChange={(_, selected) => onChange(selected.map((o) => o.value))}
      filterOptions={searchable ? undefined : (opts) => opts}
      sx={{
        width,
        "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap", overflow: "hidden" },
        "& .MuiAutocomplete-clearIndicator": { visibility: "visible" },
        "& .MuiAutocomplete-input::placeholder": { color: "text.secondary", opacity: 1 },
        ...(searchable
          ? {}
          : {
              "& .MuiAutocomplete-inputRoot, & .MuiChip-root": { cursor: "pointer" },
              // Collapse the input only when chips show; leave room for the "All" placeholder when empty.
              ...(value.length > 0
                ? {
                    "& .MuiAutocomplete-input": {
                      width: "0 !important",
                      minWidth: "0 !important",
                      flex: "0 0 0 !important",
                      paddingLeft: "0 !important",
                      paddingRight: "0 !important",
                    },
                  }
                : {}),
            }),
      }}
      slotProps={{
        popper: { style: { width: "fit-content", minWidth: width } },
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps} sx={{ "&.MuiAutocomplete-option": { whiteSpace: "nowrap", pl: 1 } }}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              sx={{ mr: 1, p: 0.5 }}
              checked={selected}
            />
            {option.label}
          </Box>
        );
      }}
      renderValue={(selected, getItemProps) => {
        const [first, ...rest] = selected;
        if (!first) return null;
        const { key, ...firstProps } = getItemProps({ index: 0 });
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flex: 1, pointerEvents: searchable ? undefined : "none" }}>
            <Chip key={key} label={first.label} size={size} sx={{ minWidth: 0 }} {...firstProps} onDelete={undefined} />
            {rest.length > 0 && (
              <Box component="span" sx={{ whiteSpace: "nowrap", color: "text.secondary", flexShrink: 0 }}>
                +{rest.length}
              </Box>
            )}
          </Box>
        );
      }}
      renderInput={({ inputProps, InputLabelProps, ...params }) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : undefined}
          slotProps={{
            htmlInput: { ...inputProps, readOnly: !searchable },
            inputLabel: { ...InputLabelProps, shrink: true },
          }}
        />
      )}
    />
  );
}
